import React, { useState, useEffect, useRef, useCallback } from "react";
import {
  TableColumn,
  StandardizedTable,
  formatNumber,
  formatCurrency,
  TableRow,
} from "@/components/ui/standardized-table";
import { useGlobalFilter } from "@/features/filters/filter.context";
import { FilterState, formatDateToISO } from "@/features/filters/filter.types";
import { filtersAreEqual as compareFilters } from "@/features/filters/filter.utils";
import { useAccount } from "@/contexts/account.context";
import type { TableQueryDTO } from "@/types/table-query";
import { useDrilldownOptional } from "@/features/drilldown";
import { getDailyControlApiV1DailyControlCardGet } from "@/backend-sdk/client/sdk.gen";
import type {
  DailyControlCardResponse,
  GetDailyControlApiV1DailyControlCardGetData,
} from "@/backend-sdk/client/types.gen";

type DailyControlRow = DailyControlCardResponse["items"][number];
type DailyControlQuery = GetDailyControlApiV1DailyControlCardGetData["query"];
type DailyControlCrossFilterKey = Extract<
  keyof DailyControlQuery,
  `filter_${string}`
>;
type DailyControlCrossFilters = Pick<
  DailyControlQuery,
  DailyControlCrossFilterKey
>;

const getDailyControlCrossFilters = (
  filters: Record<string, string> | undefined,
): Partial<DailyControlCrossFilters> => ({
  filter_search_term: filters?.filter_search_term,
  filter_device: filters?.filter_device,
  filter_region: filters?.filter_region,
  filter_city: filters?.filter_city,
  filter_category: filters?.filter_category,
  filter_product: filters?.filter_product,
});

const ControleDiarioTable: React.FC = () => {
  const { selectedAccount } = useAccount();
  const { filter: globalFilter } = useGlobalFilter();
  const drilldown = useDrilldownOptional();
  const [data, setData] = useState<DailyControlCardResponse | null>(null);
  // Começa carregando: a busca só sai no efeito, e a tabela vazia antes dela parecia "sem dados".
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const lastFilterRef = useRef<FilterState | null>(null);
  const lastCrossFiltersKeyRef = useRef<string>("");
  const [page, setPage] = useState<number>(1);
  const PAGE_SIZE = 10;
  const [tableQuery, setTableQuery] = useState<TableQueryDTO>({
    sortBy: "data",
    sortDir: "desc",
  });

  const parseDateToISO = (raw: unknown): string | null => {
    if (raw === null || raw === undefined) return null;
    const value = String(raw).trim();
    // ISO: YYYY-MM-DD
    if (/^\d{4}-\d{2}-\d{2}$/.test(value)) {
      return value;
    }
    // BR: DD/MM/YYYY
    const brMatch = value.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
    if (brMatch) {
      const [, dd, mm, yyyy] = brMatch;
      return `${yyyy}-${mm}-${dd}`;
    }
    return null;
  };

  // Handler para clique na linha (cross-filtering por data com Ctrl+click multi-select)
  const handleRowClick = useCallback(
    (row: TableRow, index: number, event?: React.MouseEvent) => {
      if (!drilldown) return;

      const rawDate = row.data ?? row.date;
      const isoDate = parseDateToISO(rawDate);
      if (!isoDate) return;

      const isMultiSelect = !!(event?.ctrlKey || event?.metaKey);
      drilldown.selectRow("date", isoDate, String(rawDate), isMultiSelect);
    },
    [drilldown],
  );

  const isRowSelected = useCallback(
    (row: TableRow, index: number): boolean => {
      if (!drilldown) return false;
      const rawDate = row.data ?? row.date;
      const isoDate = parseDateToISO(rawDate);
      if (!isoDate) return false;
      return drilldown.isSelected("date", isoDate);
    },
    [drilldown],
  );

  // Função para buscar dados do Daily Control
  const fetchDailyControlData = useCallback(
    async (filter: FilterState, pg: number = 1) => {
      // Sem validação de conta selecionada: usar customer_uuid de conta ou localStorage

      try {
        setLoading(true);
        setError(null);

        // Formatar datas para o formato esperado pela API (YYYY-MM-DD) no timezone local
        const startDate = formatDateToISO(filter.dateRange.startDate);
        const endDate = formatDateToISO(filter.dateRange.endDate);

        console.log("🔍 Buscando dados do Controle Diário:", {
          startDate,
          endDate,
          period: filter.period,
        });

        // Usar customer_uuid do selectedAccount ou fallback para o ID da conta (que funciona)
        const customerUuid =
          selectedAccount?.customer_uuid ||
          selectedAccount?.id ||
          localStorage.getItem("active_customer_uuid") ||
          undefined;
        console.log(
          "🔍 Usando customerUuid para Controle Diário:",
          customerUuid,
        );

        if (!customerUuid) return;

        console.log(
          "✅ customerUuid configurado corretamente para Controle Diário:",
          customerUuid,
        );

        const { data: dailyControlData } =
          await getDailyControlApiV1DailyControlCardGet({
            query: {
              start_date: startDate,
              end_date: endDate,
              period: filter.period,
              comparison: filter.comparison?.type || "previous_period",
              comparison_label: filter.comparison?.label,
              custom_start_date:
                filter.period === "custom" && filter.customDateRange?.startDate
                  ? formatDateToISO(filter.customDateRange.startDate)
                  : undefined,
              custom_end_date:
                filter.period === "custom" && filter.customDateRange?.endDate
                  ? formatDateToISO(filter.customDateRange.endDate)
                  : undefined,
              customer_uuid: customerUuid,
              page: pg,
              page_size: PAGE_SIZE,
              q: tableQuery.q,
              sort_by: tableQuery.sortBy,
              sort_dir: tableQuery.sortDir,
              filters: tableQuery.filters?.length
                ? JSON.stringify(tableQuery.filters)
                : undefined,
              ...getDailyControlCrossFilters(drilldown?.apiFilters),
            },
            throwOnError: true,
          });

        setData(dailyControlData);
        console.log(
          "✅ Dados do Controle Diário carregados:",
          dailyControlData,
        );
      } catch (err) {
        console.error("❌ Erro ao buscar dados do Controle Diário:", err);
        setError(
          err instanceof Error
            ? err.message
            : "Erro ao carregar dados do Controle Diário",
        );
      } finally {
        setLoading(false);
      }
    },
    [selectedAccount, tableQuery, drilldown],
  );

  useEffect(() => {
    if (!globalFilter) return;

    const currentCrossFiltersKey = drilldown?.apiFiltersKey || "";
    if (
      compareFilters(lastFilterRef.current, globalFilter) &&
      lastCrossFiltersKeyRef.current === currentCrossFiltersKey
    ) {
      return;
    }

    lastCrossFiltersKeyRef.current = currentCrossFiltersKey;
    lastFilterRef.current = globalFilter;
    setPage(1);
    fetchDailyControlData(globalFilter, 1);
  }, [globalFilter, fetchDailyControlData, drilldown?.apiFiltersKey]);

  // Refetch ao mudar página ou query da tabela (server-side)
  useEffect(() => {
    if (!lastFilterRef.current) return;
    fetchDailyControlData(lastFilterRef.current, page);
  }, [page, tableQuery, fetchDailyControlData]);

  // Configuração da tabela padronizada
  const columns: TableColumn[] = [
    {
      key: "idx",
      header: "#",
      type: "number",
      align: "right",
      sortable: false,
      visible: true,
      width: "4ch",
      render: (value) => value,
    },
    {
      key: "data",
      header: "Data",
      type: "date",
      align: "left",
      sortable: true,
      visible: true,
    },
    {
      key: "sessions",
      header: "Sessões",
      sortable: true,
      type: "number",
      hasDelta: true,
      visible: true,
      render: (value) => formatNumber(value),
    },
    {
      key: "cps",
      header: "CPS",
      sortable: true,
      type: "number",
      hasDelta: true,
      visible: true,
      render: (value) => value.toFixed(2),
    },
    {
      key: "transactions",
      header: "Pedidos",
      sortable: true,
      type: "number",
      hasDelta: true,
      visible: true,
    },
    {
      key: "tm",
      header: "TM",
      sortable: true,
      type: "number",
      visible: true,
      render: (value) => formatCurrency(value),
    },
    {
      key: "tx_conversion",
      header: "TX Conversão",
      sortable: true,
      type: "percent",
      visible: true,
      render: (value) => `${value.toFixed(2)}%`,
    },
    {
      key: "meta_investment",
      header: "Invest. Meta",
      sortable: true,
      type: "number",
      visible: true,
      render: (value) => formatCurrency(value),
    },
    {
      key: "google_investment",
      header: "Invest. Google",
      sortable: true,
      type: "number",
      visible: true,
      render: (value) => formatCurrency(value),
    },
    {
      key: "total_revenue",
      header: "Receita Total",
      sortable: true,
      type: "number",
      visible: true,
      render: (value) => formatCurrency(value),
    },
    {
      key: "roas",
      header: "ROAS",
      sortable: true,
      type: "number",
      visible: true,
      render: (value) =>
        Number(value || 0).toLocaleString("pt-BR", {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        }),
    },
    {
      key: "marketing_percentage",
      header: "% Marketing",
      sortable: true,
      type: "percent",
      visible: true,
      render: (value) => `${value.toFixed(2)}%`,
    },
  ];

  // Preparar dados para a tabela (manter chaves do backend para query server-side)
  const tableData = data?.items
    ? data.items.map((item: DailyControlRow, i: number) => ({
        ...item,
        idx:
          (data?.pagination?.page
            ? (data.pagination.page - 1) * PAGE_SIZE
            : 0) +
          i +
          1,
      }))
    : [];

  // Linha de total global (calculada no backend no conjunto filtrado completo)
  const totalRow = data?.total_row
    ? {
        idx: "",
        ...data.total_row,
      }
    : undefined;

  return (
    <StandardizedTable
      title="CONTROLE DIÁRIO"
      description={
        drilldown
          ? "Clique em uma data para filtrar a página inteira"
          : undefined
      }
      defaultSortBy="data"
      defaultSortDir="desc"
      columns={columns}
      data={tableData}
      loading={loading}
      total={totalRow}
      pagination={{
        current: data?.pagination?.page || page,
        pageSize: data?.pagination?.page_size || PAGE_SIZE,
        total: data?.pagination?.total || 0,
        onPageChange: (p) => {
          setPage(p);
        },
      }}
      queryMode="server"
      onQueryChange={(q) => {
        setTableQuery(q);
        setPage(1);
      }}
      onRowClick={drilldown ? handleRowClick : undefined}
      selectedRowIndex={drilldown ? isRowSelected : undefined}
      clickableRows={!!drilldown}
      button={{
        text: "ANÁLISE DIÁRIA POR CANAL",
        path: "/dashboard/analise-diaria",
      }}
    />
  );
};

export default ControleDiarioTable;
